import React, { useContext, useEffect } from "react";

import {
  Authenticator,
  useAuthenticator,
  CheckboxField,
  useTheme,
  View,
  Text,
} from "@aws-amplify/ui-react";
import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  Box,
  Button,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  signIn,
  confirmSignUp,
  SignInInput,
  fetchAuthSession,
  signOut,
} from "aws-amplify/auth";
import axios from "axios";
import { getUserAPI } from "api/user.api";
import { AuthContext } from "../../providers/userProvider";
import { AuthData } from "types/auth";


const SIGNUP_PROFILE_GATE_SESSION_KEY = "www-auth-profile-gate-signup-flow";
const ACCOUNT_ACCESS_BLOCKED_SUBSTRING = "Your account has been deactivated";

async function assertUserAllowedAfterCognitoSignIn(): Promise<void> {
  const { tokens } = await fetchAuthSession({ forceRefresh: true });
  const sub = tokens?.idToken?.payload?.sub as string | undefined;
  if (!sub) {
    return;
  }
  try {
    await getUserAPI(sub);
  } catch (e) {
    if (
      axios.isAxiosError(e) &&
      e.response?.status === 403 &&
      typeof (e.response?.data as { message?: string })?.message === "string" &&
      (e.response.data as { message: string }).message.includes(
        ACCOUNT_ACCESS_BLOCKED_SUBSTRING
      )
    ) {
      await signOut();
      throw new Error((e.response.data as { message: string }).message);
    }
    return;
  }
}

interface AmplifyAuthenticatorProps {
  open: boolean;
  goToSignUp: boolean;
  handleClose: () => void;
}
const AmplifyAuthenticator: React.FC<AmplifyAuthenticatorProps> = ({
  open,
  goToSignUp,
  handleClose,
}) => {
  const navigate = useNavigate();
  const { signOut, toSignUp, toSignIn, route } = useAuthenticator((context) => [
    context.route,
  ]);
  const { userData, isAccountRejected, setIsAccountRejected } =
    useContext<AuthData>(AuthContext);
  const signin = async () => {
    handleClose();
    if (userData?.user?.userId === undefined) {
      signOut();
      navigate(`/signup/${userData?.sub}`, {
        state: { sub: userData?.sub, email: userData?.email },
      });
    }
  };
  useEffect(() => {
    if (userData?.user) {
      handleClose();
      signin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user]);
  useEffect(() => {
    if (goToSignUp === true) {
      setTimeout(() => {
        toSignUp();
      }, 10);
    } else {
      toSignIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToSignUp]);
  const onClose = () => {
    if (["forgotPassword", "signIn", "signUp"].includes(route)) {
      handleClose();
    }
    setTimeout(() => {
      setIsAccountRejected(false);
    }, 1000);
  };
  const passwordSettings = {
    minLength: 8,
    requireLowercase: true,
    requireUppercase: true,
    requireNumbers: true,
    requireSpecialCharacters: true,
  };
  const formFields = {
    signIn: {
      username: {
        placeholder: "Enter your email",
        isRequired: true,
        label: "Email:",
      },
    },
    signUp: {
      username: {
        placeholder: "Enter your email",
        isRequired: true,
        label: "Email:",
      },
    },
    forgotPassword: {
      username: {
        placeholder: "Enter Your Email Here",
        isRequired: true,
        label: "Email:",
      },
    },
  };
  const components = {
    SignUp: {
      FormFields() {
        const { validationErrors } = useAuthenticator();
        return (
          <>
            {/* Re-use default `Authenticator.SignUp.FormFields` */}
            <Authenticator.SignUp.FormFields />

            {/* Append & require Terms & Conditions field to sign up  */}
            <CheckboxField
              value="yes"
              errorMessage={validationErrors.acknowledgement as any}
              hasError={!!validationErrors.acknowledgement}
              name="acknowledgement"
              isRequired={false}
              label={
                <>
                  Accepting {" "}
                  <a href='/agreement-policies'
                    target='_blank'>program guidelines</a>{" "}  and{" "}
                  <a href='/policy'
                    target='_blank'>privacy policy</a>{" "}
                </>
              }
            />
          </>
        );
      },
      Footer() {
        const { tokens } = useTheme();

        return (
          <View
            textAlign="center"
            paddingInlineEnd={tokens.space.small}
            paddingInlineStart={tokens.space.small}
          >
            <Text color={tokens.colors.neutral[80]}>
              We're using an external service to keep your email and password
              secure
            </Text>
          </View>
        );
      },
    },

  };
  const services = {
    async validateCustomSignUp(formData: any) {
      if (
        "acknowledgement" in formData &&
        formData?.acknowledgement === undefined
      ) {
        return {
          acknowledgement: "You must agree to the Terms & Conditions",
        };
      }
      if (
        ["username", "password", "confirm_password"].every(
          (t) => t in formData
        ) &&
        !("acknowledgement" in formData)
      ) {
        return {
          acknowledgement: "You must agree to the Terms & Conditions",
        };
      }
    },
    async handleSignIn(formData: { username: string; password: string }) {
      sessionStorage.removeItem(SIGNUP_PROFILE_GATE_SESSION_KEY);
      const { username, password } = formData;
      const result = await signIn({ username, password });
      if (result.nextStep.signInStep === "CONFIRM_SIGN_UP") {
        sessionStorage.setItem("username", username);
        sessionStorage.setItem("password", password);
      }

      if (result.nextStep.signInStep === "DONE") {
        await assertUserAllowedAfterCognitoSignIn();
      }

      return result;
    },
    async handleConfirmSignUp(formData: {
      username: string;
      confirmationCode: string;
    }) {
      const { username, confirmationCode } = formData;
      const result = await confirmSignUp({
        username,
        confirmationCode,
        options: { autoSignIn: true },
      });
      if (
        result.isSignUpComplete &&
        result.nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        sessionStorage.setItem(SIGNUP_PROFILE_GATE_SESSION_KEY, "1");
        return result;
      } else {
        const data = {
          username: sessionStorage.getItem("username"),
          password: sessionStorage.getItem("password"),
        } as SignInInput;
        sessionStorage.removeItem("username");
        sessionStorage.removeItem("password");
        const signInResult = await signIn(data);
        if (signInResult.nextStep.signInStep === "DONE") {
          await assertUserAllowedAfterCognitoSignIn();
        }
        sessionStorage.setItem(SIGNUP_PROFILE_GATE_SESSION_KEY, "1");
        window.location.reload();
      }
    },
  };

  return (
    <div>
      <Dialog open={open} onClose={onClose}>
        {isAccountRejected ? (
          <>
            <DialogTitle sx={{ my: 2 }}>
              {" "}
              <Typography sx={{ color: "neutral" }} variant="subtitle1">
                Account Rejected
              </Typography>{" "}
            </DialogTitle>
            <DialogContent>
              <Typography variant="bodyCopyRegular" gutterBottom>
                We're sorry, but it appears that the email you used has already
                been rejected. Please <Link target="_blank" href={`mailto:admin@netafoundation.org`} underline="always" color={'inherit'} sx={{
                  "&:hover": {
                    cursor: "pointer",
                  }
                }}> contact us</Link> for further assistance. Thank
                you for your understanding.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "row", pt: 4 }}>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => onClose()}
                >
                  Got it
                </Button>
              </Box>
            </DialogContent>
          </>
        ) : (
          <Authenticator
            passwordSettings={passwordSettings}
            services={services as any}
            formFields={formFields}
            components={components}
          />
        )}
      </Dialog>
    </div>
  );
};
export default AmplifyAuthenticator;
